// Tarot card definitions and helper utilities.
//
// Each tarot card exported from this module defines two orientations: 'right'
// (upright) and 'wrong' (upside down). Each orientation includes a display
// name, a reference to the image asset (imported below), a brief description
// for tooling and debug purposes, and an `apply` function that accepts a
// context object. The context exposes setter functions defined in the
// App component (see App.js) that can be used to modify exploration state
// when the card is drawn. Effects that are flagged as permanent will
// persist across floors. All other effects are reset whenever a new floor
// is entered.

// NOTE: the numeric prefix on the image filenames matches the ordering
// provided by the user. If you rename or add additional cards, ensure the
// corresponding images are imported here and that the `id` fields remain
// unique.

import pilgrimImg from '../assets/tarot/0_the_pilgrim.png';
import contractImg from '../assets/tarot/1_the_contract.png';
import veilImg from '../assets/tarot/2_the_veil.png';
import gildedHostImg from '../assets/tarot/3_the_gilded_host.png';
import ironBannerImg from '../assets/tarot/4_the_iron_banner.png';
import reliquaryImg from '../assets/tarot/5_the_reliquary.png';
import torchbearerImg from '../assets/tarot/9_the_torchbearer.png';

// Additional tarot card artwork for extended card pool. These placeholder images
// currently live alongside the existing assets. The numeric prefixes reflect
// the ordering provided by the design spec.
import caravanImg from '../assets/tarot/7_the_caravan.png';
import boneDieImg from '../assets/tarot/10_the_bone_die.png';
import vanishedImg from '../assets/tarot/12_the_vanished.png';
import huskImg from '../assets/tarot/13_the_husk.png';
import crucibleImg from '../assets/tarot/14_the_crucible.png';
import chainImg from '../assets/tarot/15_the_chain.png';

// Starting floor at which tarot cards begin to appear. This value can be
// tweaked from other modules (e.g. via development tools) to adjust when
// tarot mechanics activate. Floors below this threshold will not draw
// cards when descending stairs.
export const TAROT_START_FLOOR = 5;

/**
 * Definition of all tarot cards. Each entry has a stable `id` and an
 * `orientations` object that contains the upright (`right`) and inverted
 * (`wrong`) variants. Both orientations must define an `apply` callback
 * that mutates exploration state via the provided context setters.
 */
export const tarotCards = [
  {
    id: 'pilgrim',
    name: 'The Pilgrim',
    image: pilgrimImg,
    orientations: {
      right: {
        title: 'The Pilgrim (Upright)',
        description: 'Heal 1 HP for each walked tile.',
        apply(ctx) {
          // Grant 1 HP per tile walked until the next floor.
          if (ctx.setTarotPerTileHpHeal) ctx.setTarotPerTileHpHeal(1);
        },
        permanent: false,
      },
      wrong: {
        title: 'The Pilgrim (Reversed)',
        description: 'Traps have a shorter reaction time.',
        apply(ctx) {
          // Reduce trap quick-time event duration by 25% (multiplicative).
          if (ctx.setTarotTrapTimeFactor) ctx.setTarotTrapTimeFactor(0.75);
        },
        permanent: false,
      },
    },
  },
  {
    id: 'contract',
    name: 'The Contract',
    image: contractImg,
    orientations: {
      right: {
        title: 'The Contract (Upright)',
        description: 'You survive your first defeat.',
        apply(ctx) {
          // Allow the player to survive a lethal hit once. The
          // implementation of the extra life check lives in App.js.
          if (ctx.setTarotExtraLife) ctx.setTarotExtraLife(true);
        },
        permanent: false,
      },
      wrong: {
        title: 'The Contract (Reversed)',
        description: 'Lose all your teeth.',
        apply(ctx) {
          // Immediately remove all teeth on draw. Because the effect is
          // permanent, we do not reset this at floor change.
          if (ctx.setTeeth) ctx.setTeeth(0);
        },
        permanent: true,
      },
    },
  },
  {
    id: 'veil',
    name: 'The Veil',
    image: veilImg,
    orientations: {
      right: {
        title: 'The Veil (Upright)',
        description: 'You skip all combats.',
        apply(ctx) {
          // Enable unlimited bypass of enemies (skip combats) on this floor.
          if (ctx.setTarotBypassCombats) ctx.setTarotBypassCombats(true);
        },
        permanent: false,
      },
      wrong: {
        title: 'The Veil (Reversed)',
        description: 'Enemies move towards you.',
        apply(ctx) {
          // Flag that enemies should attempt to move towards the player
          // after each player movement. Actual movement logic lives in
          // App.js.
          if (ctx.setTarotEnemiesChase) ctx.setTarotEnemiesChase(true);
        },
        permanent: false,
      },
    },
  },
  {
    id: 'gilded_host',
    name: 'The Gilded Host',
    image: gildedHostImg,
    orientations: {
      right: {
        title: 'The Gilded Host (Upright)',
        description: 'Shops have rare items.',
        apply(ctx) {
          if (ctx.setTarotShopRarity) ctx.setTarotShopRarity('more');
        },
        permanent: false,
      },
      wrong: {
        title: 'The Gilded Host (Reversed)',
        description: 'Shops have less rare items.',
        apply(ctx) {
          if (ctx.setTarotShopRarity) ctx.setTarotShopRarity('less');
        },
        permanent: false,
      },
    },
  },
  {
    id: 'iron_banner',
    name: 'The Iron Banner',
    image: ironBannerImg,
    orientations: {
      right: {
        title: 'The Iron Banner (Upright)',
        description: 'Start each combat with 20 Armor.',
        apply(ctx) {
          if (ctx.setTarotPlayerStartArmor) ctx.setTarotPlayerStartArmor(20);
        },
        permanent: false,
      },
      wrong: {
        title: 'The Iron Banner (Reversed)',
        description: 'Enemies start each combat with 20 Armor.',
        apply(ctx) {
          if (ctx.setTarotEnemyStartArmor) ctx.setTarotEnemyStartArmor(20);
        },
        permanent: false,
      },
    },
  },
  {
    id: 'reliquary',
    name: 'The Reliquary',
    image: reliquaryImg,
    orientations: {
      right: {
        title: 'The Reliquary (Upright)',
        description: 'Gain permanently 1 random Trinket.',
        apply(ctx) {
          if (ctx.addRandomTrinket) ctx.addRandomTrinket();
        },
        permanent: true,
      },
      wrong: {
        title: 'The Reliquary (Reversed)',
        description: 'Lose the last Trinket you picked (non‑quest Trinkets).',
        apply(ctx) {
          if (ctx.removeLastTrinket) ctx.removeLastTrinket();
        },
        permanent: true,
      },
    },
  },
  {
    id: 'torchbearer',
    name: 'The Torchbearer',
    image: torchbearerImg,
    orientations: {
      right: {
        title: 'The Torchbearer (Upright)',
        description: "You don't need torches.",
        apply(ctx) {
          if (ctx.setTarotNoTorchesNeeded) ctx.setTarotNoTorchesNeeded(true);
        },
        permanent: false,
      },
      wrong: {
        title: 'The Torchbearer (Reversed)',
        description: 'Decrease your maximum light radius.',
        apply(ctx) {
          if (ctx.setTarotLightRadiusPenalty) ctx.setTarotLightRadiusPenalty(1);
        },
        permanent: false,
      },
    },
  },

  // --- Extended tarot cards (added for agent tasks) ---
  {
    id: 'caravan',
    name: 'The Caravan',
    image: caravanImg,
    orientations: {
      right: {
        title: 'The Caravan (Upright)',
        description: 'Free trinkets at the Merchant!',
        apply(ctx) {
          // When active, all trinkets offered by the merchant cost 0 teeth on this floor.
          if (ctx.setTarotFreeTrinkets) ctx.setTarotFreeTrinkets(true);
        },
        permanent: false,
      },
      wrong: {
        title: 'The Caravan (Reversed)',
        description: "No Merchant on this floor.",
        apply(ctx) {
          // Prevent the merchant from appearing or stocking goods this floor.
          if (ctx.setTarotNoMerchant) ctx.setTarotNoMerchant(true);
        },
        permanent: false,
      },
    },
  },
  {
    id: 'bone_die',
    name: 'The Bone Die',
    image: boneDieImg,
    orientations: {
      right: {
        title: 'The Bone Die (Upright)',
        description: 'One extra reward choice.',
        apply(ctx) {
          // Increase the number of reward choices by 1 on this floor.
          if (ctx.setTarotExtraRewardChoice) ctx.setTarotExtraRewardChoice(1);
        },
        permanent: false,
      },
      wrong: {
        title: 'The Bone Die (Reversed)',
        description: 'Single-item rewards.',
        apply(ctx) {
          // Limit rewards to a single choice (no alternatives) on this floor.
          if (ctx.setTarotReducedRewardChoice) ctx.setTarotReducedRewardChoice(true);
        },
        permanent: false,
      },
    },
  },
  {
    id: 'vanished',
    name: 'The Vanished',
    image: vanishedImg,
    orientations: {
      right: {
        title: 'The Vanished (Upright)',
        description: "The Archmage's presence echoes in this floor",
        apply(ctx) {
          // Flag to boost magic damage/effects for both sides on this floor.
          if (ctx.setTarotMagicModifier) ctx.setTarotMagicModifier('boost');
        },
        permanent: false,
      },
      wrong: {
        title: 'The Vanished (Reversed)',
        description: "The Archmage's absence echoes in this floor.",
        apply(ctx) {
          // Flag to weaken magic damage/effects for both sides on this floor.
          if (ctx.setTarotMagicModifier) ctx.setTarotMagicModifier('nerf');
        },
        permanent: false,
      },
    },
  },
  {
    id: 'husk',
    name: 'The Husk',
    image: huskImg,
    orientations: {
      right: {
        title: 'The Husk (Upright)',
        description: 'A sense of familiarity overtakes you.',
        apply(ctx) {
          // Reveal the entire floor map regardless of light radius.
          if (ctx.setTarotRevealMap) ctx.setTarotRevealMap(true);
        },
        permanent: false,
      },
      wrong: {
        title: 'The Husk (Reversed)',
        description: 'Darkness damages HP.',
        apply(ctx) {
          // Enable HP damage over time when in darkness on this floor.
          if (ctx.setTarotDarknessDamage) ctx.setTarotDarknessDamage(1);
        },
        permanent: false,
      },
    },
  },
  {
    id: 'crucible',
    name: 'The Crucible',
    image: crucibleImg,
    orientations: {
      right: {
        title: 'The Crucible (Upright)',
        description: 'Start each combat with 50% reflect.',
        apply(ctx) {
          // Grant the player a 50% reflect shield at the start of each combat this floor.
          if (ctx.setTarotPlayerStartReflectPct) ctx.setTarotPlayerStartReflectPct(50);
        },
        permanent: false,
      },
      wrong: {
        title: 'The Crucible (Reversed)',
        description: 'Enemies start each combat with 50% reflect.',
        apply(ctx) {
          // Grant enemies a 50% reflect shield at the start of each combat this floor.
          if (ctx.setTarotEnemyStartReflectPct) ctx.setTarotEnemyStartReflectPct(50);
        },
        permanent: false,
      },
    },
  },
  {
    id: 'chain',
    name: 'The Chain',
    image: chainImg,
    orientations: {
      right: {
        title: 'The Chain (Upright)',
        description: 'Consecutive victories empower you.',
        apply(ctx) {
          // Award increasing experience for each enemy defeated on this floor; level up if all are defeated.
          if (ctx.setTarotChainExpBonus) ctx.setTarotChainExpBonus(true);
        },
        permanent: false,
      },
      wrong: {
        title: 'The Chain (Reversed)',
        description: 'You gain no experience from combat.',
        apply(ctx) {
          // Suppress experience gains from combat on this floor.
          if (ctx.setTarotNoExp) ctx.setTarotNoExp(true);
        },
        permanent: false,
      },
    },
  },
];

/**
 * Randomly select a tarot card orientation from those that have not yet
 * appeared this run. Once an orientation has been drawn it is removed
 * from the pool so it cannot be repeated later. Returns both the card
 * definition and the orientation key. If the pool is exhausted, null
 * is returned.
 *
 * @param {Array} history Array of previously-drawn { id, orientation } entries
 */
export function drawTarotCard(history = []) {
  const usedSet = new Set(history.map((h) => `${h.id}:${h.orientation}`));
  const options = [];
  for (const card of tarotCards) {
    for (const orientation of ['right', 'wrong']) {
      const key = `${card.id}:${orientation}`;
      if (!usedSet.has(key)) options.push({ card, orientation });
    }
  }
  if (!options.length) return null;
  const idx = Math.floor(Math.random() * options.length);
  return options[idx];
}